import { useState } from "react";
import SectionHeader from "@/components/SectionHeader";
import { submitContact, DISCORD_URL } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageCircle, MapPin } from "lucide-react";
import { sfx } from "@/lib/sounds";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      sfx.error();
      return toast.error("Fill all fields");
    }
    setSending(true);
    try {
      await submitContact(form);
      sfx.levelup();
      toast.success("Message sent!", { description: "We'll reply within 48 hours." });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      sfx.error();
      toast.error("Failed to send. Try Discord instead.");
    }
    setSending(false);
  };

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-contact">
      <div className="max-w-5xl mx-auto">
        <SectionHeader eyebrow="Contact" title="Get In Touch" subtitle="Bug reports, ban appeals, partnerships or just to say hi." />
        <div className="grid md:grid-cols-5 gap-6">
          <form onSubmit={submit} className="md:col-span-3 rounded-2xl glass p-6 space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-white/50">Name</span>
                <input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={set("name")}
                  className="mt-1 w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 focus:border-[#FFBF00] outline-none"
                />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-white/50">Email</span>
                <input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  className="mt-1 w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 focus:border-[#FFBF00] outline-none"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-white/50">Subject</span>
              <input
                data-testid="contact-subject"
                value={form.subject}
                onChange={set("subject")}
                className="mt-1 w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 focus:border-[#FFBF00] outline-none"
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-white/50">Message</span>
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={set("message")}
                rows={6}
                className="mt-1 w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 focus:border-[#FFBF00] outline-none resize-none"
              />
            </label>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={sending}
              className="px-6 py-3 rounded-full bg-[#FFBF00] text-black font-semibold text-sm uppercase tracking-wider hover:bg-[#FF8C00] transition-colors disabled:opacity-60"
            >
              {sending ? "Sending..." : "Send Message"}
            </button>
          </form>

          <div className="md:col-span-2 space-y-4">
            <div className="rounded-2xl glass p-5">
              <div className="flex items-center gap-3 text-[#FFBF00]"><MessageCircle size={18} /> Discord</div>
              <p className="text-sm text-white/60 mt-2">Fastest reply — open a ticket in #support.</p>
              <a href={DISCORD_URL} target="_blank" rel="noreferrer" data-testid="contact-discord" className="mt-3 inline-flex text-sm text-[#FFBF00] hover:underline">Join Discord →</a>
            </div>
            <div className="rounded-2xl glass p-5">
              <div className="flex items-center gap-3 text-[#FFBF00]"><Mail size={18} /> Email</div>
              <p className="text-sm text-white/60 mt-2">support@lostdrkc.in</p>
            </div>
            <div className="rounded-2xl glass p-5">
              <div className="flex items-center gap-3 text-[#FFBF00]"><MapPin size={18} /> HQ</div>
              <p className="text-sm text-white/60 mt-2">Bengaluru, India · Running 24/7</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
